// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LegacyManufacturer, { LegacyManufacturerStyles } from "@flip/App/Components/Product/LegacyManufacturer";
import setRequestQuoteModal from "@flip/App/Handlers/RequestQuoteModal/RequestQuoteModalHandler";
import cartPageStyles from "@flip/App/SharedStyle/CartPageStyles";
import { CustomAppState } from "@flip/App/Store/Reducers";
import ProductBrand from "@flip/Overrides/Components/ProductBrand";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@flip/Overrides/Components/ProductPartNumbers";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color, parseBoolean, xScreen } from "@flip/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Checkbox, GridContainer, GridItem } from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { setCookie } from "@insite/mobius/utilities/cookies";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: QuoteRequiredModalStyles;
}

const mapStateToProps = (state: CustomAppState) => ({
    modalIsOpen: state.xRequestQuoteModal.isOpen,
    productContext: state.xRequestQuoteModal.productContext,
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    setRequestQuoteModal,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface QuoteRequiredModalStyles {
    modal?: ModalPresentationProps;
    container?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    productPopupInfo?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    imageContainer?: InjectableCss;
    actionsContainer?: InjectableCss;
    veiwCartButton?: ButtonProps;
    continueCartButton?: ButtonProps;
    titleText?: TypographyPresentationProps;
    messageText?: TypographyPresentationProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    // XNG-Changes
    legacyManufacturerStyles?: LegacyManufacturerStyles;
}
export const quoteRequiredModalStyles: QuoteRequiredModalStyles = {
    modal: {
        sizeVariant: "large",
        mergeCss: true,
        cssOverrides: {
            modalBody: css`
                border-radius: 4px;
                ${xScreen("gt", "md")} {
                    max-width: 770px;
                }

                ${xScreen("lt", "md")} {
                    max-width: 330px;
                }

                &&&&& {
                    box-shadow: none;
                }
            `,
            modalContent: css`
                &&&& {
                    padding: 32px 16px 24px 16px;

                    ${xScreen("lt", "md")} {
                        padding: 32px 16px 16px 16px;
                    }
                }
            `,
            modalTitle: css`
                &&&& {
                    padding: 0;
                }
            `,
        },
    },
    container: {
        css: css`
            display: flex;
            justify-content: space-between;

            ${xScreen("lt", "md")} {
                flex-direction: column;
            }
        `,
    },
    leftSection: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
            padding-right: 19px;
            border-right: 1px solid ${color("common.border")};
            ${xScreen("lt", "md")} {
                padding-right: 0;
                padding-bottom: 16px;
                border-right: none;
                border-bottom: 1px solid ${color("common.border")};
            }
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            padding-left: 19px;
            flex: 0 0 319px;

            ${xScreen("lt", "md")} {
                flex: 1;
                padding-left: 0;
                padding-top: 16px;
            }
        `,
    },
    productPopupInfo: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 91px;
                height: 91px;
                margin-right: 12px;
                flex: 0 0 91px;
            `,
        },
        img: {
            width: 91,
            height: 91,
            css: css`
                max-height: 100%;
                height: auto;
            `,
        },
    },
    actionsContainer: {
        css: css`
            display: flex;
            margin-top: 30px;
        `,
    },
    veiwCartButton: {
        variant: "secondary",
        css: css`
            margin-right: 32px;

            ${xScreen("lt", "md")} {
                margin-right: 24px;
            }
        `,
    },
    continueCartButton: {
        variant: "primary",
    },
    titleText: {
        variant: "h4",
        css: css`
            margin-bottom: 12px;
        `,
    },
    messageText: {
        size: 14,
        lineHeight: "22px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 12px;
        `,
    },
    productDescriptionStyles: {
        ...cartPageStyles.cartLine?.productDescription,
    },
    productPartNumbers: {
        ...cartPageStyles.cartLine?.productPartNumbers,
    },
};

const RequestQuoteModal = ({
    productContext,
    modalIsOpen,
    setRequestQuoteModal,
    extendedStyles,
    history,
    cartPageUrl,
}: Props) => {
    const [styles] = useState(() => mergeToNew(quoteRequiredModalStyles, extendedStyles));
    const [skipModal, setSkipModal] = useState(false);
    const [skipModalDecision, setSkipModalDecision] = useState(true);

    useEffect(() => {
        setSkipModalDecision(parseBoolean(window.localStorage.getItem("skip-request-quote-modal") || "false"));
    });

    if (skipModalDecision || !productContext) {
        return null;
    }

    const { product } = productContext;

    const modalCloseHandler = () => {
        setRequestQuoteModal({ modalIsOpen: false, skipModal });
    };

    const viewCartbuttonHandler = () => {
        if (!history || !cartPageUrl) {
            return;
        }
        setRequestQuoteModal({ modalIsOpen: false, skipModal });
        history.push(cartPageUrl);
    };

    return (
        <Modal {...styles.modal} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.leftSection}>
                    <Typography {...styles.titleText}>{translate("Quote Required")}</Typography>
                    <Typography {...styles.messageText}>{siteMessage("Flip_Request_Quote_Popup")}</Typography>
                    <Checkbox checked={skipModal} onChange={(e, value) => setSkipModal(value)}>
                        {translate("Do not show this again")}
                    </Checkbox>
                    <StyledWrapper {...styles.actionsContainer}>
                        <Button onClick={viewCartbuttonHandler} {...styles.veiwCartButton}>
                            {translate("View Cart")}
                        </Button>
                        <Button onClick={modalCloseHandler} {...styles.continueCartButton}>
                            {translate("Continue Shopping")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    <StyledWrapper {...styles.productPopupInfo}>
                        {product.brand && (
                            <ProductBrand
                                brand={product.brand}
                                isAuthorized={parseBoolean(product.properties.isAuthorizedBrand)}
                            />
                        )}
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />

                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            sku={product.detail?.sku}
                            extendedStyles={styles.productPartNumbers}
                        />
                        {/* XNG-Changes */}
                        {product.properties?.legacyManufacturer && (
                            <LegacyManufacturer
                                legacyManufacturer={product.properties.legacyManufacturer}
                                legacyManufacturerLink={product.properties.legacyManufacturerDetailUrl}
                                isAuthorized={parseBoolean(
                                    product.properties.isLegacyManufacturerAnAuthorizedSupplier,
                                )}
                                extendedStyles={styles.legacyManufacturerStyles}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(RequestQuoteModal));
